// RefineProgress.tsx — server refinement progress (bundle adjust → optimize →
// [Synthetic Enhancement: render → fix → train] → segment → done) as a labelled progress bar with the live PSNR and splat
// count. Fed by /live progress messages, falling back to the Scan's own
// `refine` snapshot before the first message arrives. Server-mode captures
// show the §15.4.1 server_tracking numbers (frames, keyframes, lag) first.
import ProgressBar from 'react-bootstrap/ProgressBar';
import type { RefineInfo } from '../api';
import type { ProgressMessage } from '../live';
import { fmtCount } from '../format';

const PHASE_LABEL: Record<string, string> = {
  server_tracking: 'Tracking the uploaded video on the server',
  bundle_adjust: 'Bundle adjusting cameras',
  optimize: 'Optimizing splats',
  synthetic_render: 'Rendering virtual views',
  synthetic_fix: 'Synthetic Enhancement',
  synthetic_train: 'Training on synthetic views',
  segment: 'Separating object from background',
  done: 'Done',
  error: 'Failed',
};

export interface RefineProgressProps {
  progress: ProgressMessage | null;
  fallback?: RefineInfo | null;
}

// serverTrackingDetail summarises a §15.4.1 server_tracking progress message.
function serverTrackingDetail(p: ProgressMessage | null): string {
  if (!p) return '';
  const parts = [`${p.frames ?? 0} frames`, `${p.keyframes ?? 0} keyframes`];
  if (p.video_s !== undefined) parts.push(`${p.video_s.toFixed(1)} s of video`);
  if (p.lag_s !== undefined) parts.push(`lag ${p.lag_s.toFixed(1)} s`);
  if (p.skipped) parts.push(`${p.skipped} skipped`);
  return parts.join(' · ');
}

export function RefineProgress({ progress, fallback }: RefineProgressProps) {
  const phase = progress?.phase ?? fallback?.phase ?? '';
  const iter = progress?.iter ?? fallback?.iter ?? 0;
  const total = progress?.total_iters ?? fallback?.total_iters ?? 0;
  const psnr = progress?.psnr ?? fallback?.psnr ?? 0;
  const pct = total > 0 ? Math.min(100, (iter / total) * 100) : phase === 'done' ? 100 : 0;
  const indeterminate = phase === 'bundle_adjust' || phase === 'segment' || phase === 'server_tracking' || (total === 0 && phase !== 'done');
  return (
    <div>
      <div className="d-flex justify-content-between small mb-1">
        <span>{PHASE_LABEL[phase] ?? (phase || 'Waiting for the refiner')}</span>
        <span className="text-body-secondary">
          {phase === 'server_tracking' && serverTrackingDetail(progress)}
          {total > 0 && `${iter}/${total}`}
          {psnr > 0 && ` · ${psnr.toFixed(1)} dB`}
          {progress?.count ? ` · ${fmtCount(progress.count)} splats` : ''}
        </span>
      </div>
      <ProgressBar
        now={indeterminate ? 100 : pct}
        animated={indeterminate || phase === 'optimize' || phase === 'synthetic_train'}
        striped={indeterminate}
        variant={phase === 'error' ? 'danger' : 'primary'}
      />
      {progress?.message && <div className="small text-body-secondary mt-1">{progress.message}</div>}
    </div>
  );
}
